import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { zodResolver } from "@hookform/resolvers/zod"
import { addDays, startOfDay } from "date-fns"
import { Button } from "@/components/ui/button"
import { DatePicker, VISUAL_DATE_FORMAT } from "@/components/ui/date-picker"
import { Dialog, DialogContentCustom, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Form, FormField } from "@/components/ui/form"
import { DateOnly } from "@/lib/date-only"
import { REFERRAL_SLIP_DEFAULT_DURATION_DAYS } from "@/modules/admin/better-doc/constants/better-doc-constants"
import { collectBetterDocErrorMessages } from "@/modules/admin/better-doc/lib/collect-better-doc-error-messages"
import {
  BetterDocReferralSlipSchema,
  betterDocReferralSlipSchema,
} from "@/modules/admin/better-doc/schema/better-doc-referral-slip-schema"
import { useSendBetterDocReferralSlipMutation } from "@/modules/admin/better-doc/services/useSendBetterDocReferralSlipMutation"
import { BetterDocSessionActionOutcome } from "@/modules/admin/better-doc/types/BetterDocSessionActionOutcome"

type BetterDocReferralSlipDialogProps = {
  sessionId: number
  onClose: () => void
  onCompleted: (outcome: BetterDocSessionActionOutcome) => void
}

const buildDefaultReferralSlipValues = (): BetterDocReferralSlipSchema => {
  const startDate = startOfDay(addDays(new Date(), 1))
  return { startDate, endDate: addDays(startDate, REFERRAL_SLIP_DEFAULT_DURATION_DAYS) }
}

export const BetterDocReferralSlipDialog = ({ sessionId, onClose, onCompleted }: BetterDocReferralSlipDialogProps) => {
  const { t } = useTranslation()
  const [sendReferralSlip, isSending] = useSendBetterDocReferralSlipMutation()

  const form = useForm<BetterDocReferralSlipSchema>({
    resolver: zodResolver(betterDocReferralSlipSchema),
    mode: "onChange",
    defaultValues: buildDefaultReferralSlipValues(),
  })
  const { control, handleSubmit, formState, trigger } = form

  const submitReferralSlip = ({ startDate, endDate }: BetterDocReferralSlipSchema) => {
    sendReferralSlip({
      variables: {
        input: { sessionId, startDate: new DateOnly(startDate).toString(), endDate: new DateOnly(endDate).toString() },
      },
      onCompleted: (response) => {
        const { actionResult, errors } = response.de_sendBetterDocReferralSlip
        onCompleted({ result: actionResult, errorMessages: collectBetterDocErrorMessages(errors) })
        onClose()
      },
    })
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onClose()
    }
  }

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContentCustom variant="small" data-testid="better-doc-admin-referral-slip-dialog">
        <DialogHeader>
          <DialogTitle>{t("admin.betterDoc.referralSlipDialog.title", { sessionId })}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={handleSubmit(submitReferralSlip)}>
            <div className="flex flex-col gap-4 px-6 pb-2">
              <FormField
                control={control}
                name="startDate"
                render={({ field, fieldState }) => (
                  <DatePicker
                    label={t("admin.betterDoc.referralSlipDialog.startDate")}
                    dataTestId="better-doc-admin-referral-slip-start-date"
                    readOnly={false}
                    placeholder={VISUAL_DATE_FORMAT}
                    value={field.value}
                    enableReset={false}
                    onSelect={(selectedDate) => {
                      field.onChange(selectedDate)
                      void trigger("endDate")
                    }}
                    onReset={() => field.onChange(undefined)}
                    error={fieldState.error?.message}
                    translateError
                  />
                )}
              />
              <FormField
                control={control}
                name="endDate"
                render={({ field, fieldState }) => (
                  <DatePicker
                    label={t("admin.betterDoc.referralSlipDialog.endDate")}
                    dataTestId="better-doc-admin-referral-slip-end-date"
                    readOnly={false}
                    placeholder={VISUAL_DATE_FORMAT}
                    value={field.value}
                    enableReset={false}
                    onSelect={field.onChange}
                    onReset={() => field.onChange(undefined)}
                    error={fieldState.error?.message}
                    translateError
                  />
                )}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="tertiary"
                onClick={onClose}
                data-testid="better-doc-admin-referral-slip-cancel-button">
                {t("common.button.cancel")}
              </Button>
              <Button
                type="submit"
                isLoading={isSending}
                disabled={!formState.isValid || isSending}
                data-testid="better-doc-admin-referral-slip-submit-button">
                {t("common.button.send")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContentCustom>
    </Dialog>
  )
}
